<template>
  <div>
    <alert-modal ref="confirm" cancelable=true :alertText="alertText" @confirm="confirmedDeleteEmployee" @cancel="cancelDeleteEmployee"/>
    <alert-modal ref="formInvalid" alertText="Form invalid" icon="error" />
    <picture-modal ref="pictureModal" :views="views" :config="config" @show="show" @hide="hide">
      <template slot="profile">
        <b-form class="employee-form">
          <!-- First Name -->
            <info-row class="info-main-row" :title="$t('first_name').toLowerCase()" v-bind:class="{hidden:firstNameHidden}">
              <span v-bind:class="{hidden:isEditing}">{{record.first_name}}</span>
              <b-form-input id="employee-first-name" v-bind:class="{hidden:(!isEditing)}"
                class="input-info-field" type="text" v-model="record.first_name" @change="$v.record.first_name.$touch"
                :state="firstNameValid" :value="record.first_name" :placeholder="$t('first_name')"
                aria-describedby="first-name-feedback"/>
            </info-row>

          <!-- Last Name -->
          <info-row class="info-main-row" :title="$t('last_name').toLowerCase()" v-bind:class="{hidden:lastNameHidden}">
            <span v-bind:class="{hidden:isEditing}">{{record.last_name}}</span>
            <b-form-input id="employee-last-name" v-bind:class="{hidden:(!isEditing)}"
              class="input-info-field" type="text" v-model="record.last_name" @change="$v.record.last_name.$touch"
              :state="lastNameValid" :value="record.last_name" :placeholder="$t('last_name')"
              aria-describedby="last-name-feedback" />
          </info-row>

          <!-- Email -->
          <info-row class="info-main-row" :title="$t('email').toLowerCase()" v-bind:class="{hidden:emailHidden}">
            <span v-bind:class="{hidden:isEditing}">{{record.email}}</span>
            <b-form-input id="employee-email" v-bind:class="{hidden:(!isEditing)}"
              class="input-info-field" type="email" v-model="record.email" @change="$v.record.email.$touch"
              :state="emailValid" :value="record.email" :placeholder="$t('email')"
              aria-describedby="email-feedback"/>
          </info-row>

          <!-- PIN, only on create -->
          <info-row class="info-main-row" :title="$t('pin').toLowerCase()" v-bind:class="{hidden:pinHidden}">
            <span v-bind:class="{hidden:(isEditing)}">{{record.pin ? '****' : 'Please set PIN'}}</span>
            <b-form-input onKeyPress="if(this.value.length==4) return false;" minlength="4" maxlength="4"
              inputmode="numeric" pattern="[0-9]{4}" id="employee-pin"  @change="$v.record.pin.$touch"
              v-bind:class="{hidden:(!isEditing) || isUpdating}" class="input-info-field"
              type="number" v-model="record.pin" :state="pinValid" :value="record.pin"
              aria-describedby="pin-feedback"/>
          </info-row>

          <!-- Role -->
          <info-row class="info-main-row" :title="$t('role').toLowerCase()" v-bind:class="{hidden:roleHidden}">
            <span v-bind:class="{hidden:isEditing}">{{record.role}}</span>
            <!-- <b-row> -->
            <!-- <b-col> -->
            <b-form-select :options="employeeTypes" id="employee-role" class="role-selector" @change="$v.record.employee_type_id.$touch"
              v-bind:class="{hidden:(!isEditing)}" v-model="record.employee_type_id" :state="employeeTypeIdValid"
              aria-describedby="employee-type-id-feedback" required/>
            <span v-bind:class="{hidden:!record.employee_type_id}" class="employee-description">({{employeeDescriptions()}})</span>
          </info-row>

          <info-row class="info-main-row" :title="$t('status').toLowerCase()" v-bind:class="{hidden:isActiveHidden}">
            <span v-bind:class="{hidden:isEditing}">{{((1 === record.is_active) ? "ACTIVE" : "INACTIVE")}}</span>
            <div v-bind:class="{hidden:(!isEditing)}">
              <toggle-button class="active-toggle" :value="(1===record.is_active)"
              color="#82C7EB" :sync="true" :labels="{checked:'active', unchecked:'inactive'}"
              :width="105" :height='33' style="font-size:12pt;" @change="$v.record.is_active.$touch"/>
            </div>
          </info-row>
        </b-form>
      </template>

      <template slot="reset_pin">
        <div class="pin-reset-container">
          <span>Please enter a unique 4 digit pin</span><br><br>
          <b-form>
            <b-form-group label="">
              <b-form-input class="pin-reset-input" type="password" pattern="[0-9]*" inputmode="numeric" maxlength=4
                placeholder="pin">
              </b-form-input>
            </b-form-group>
            <b-form-group label="">
              <b-form-input class="pin-reset-input" type="password" pattern="[0-9]*" inputmode="numeric" maxlength=4
                placeholder="confirm pin">
              </b-form-input>
            </b-form-group><br>
            <b-button> RESET PIN </b-button>
          </b-form>
        </div>
      </template>

      <template slot="modal-toolbar-buttons">
        <span v-if="!isEditing" v-on:click="deleteEmployee">
          <i class="fi-trash delete-employee nav-icon modal-btn" />
        </span>
        <span v-on:click="toggleEditMode">
          <i v-if="isEditing" class="fi-save create-employee nav-icon modal-btn" />
          <i v-else class="fi-pencil edit-employee nav-icon modal-btn" />
        </span>
        <span class="modal-btn" v-on:click="hide">
          <i class="fi-x cancel-employee-edit nav-icon" />
        </span>
      </template>
    </picture-modal>
  </div>
</template>

<script>
import PictureModal from "./PictureModal";
import InfoRow from "@/components/InfoRow";
import axios from "axios";
import API from "@/api";
import AlertModal from "@/components/modals/AlertModal";
import { required, minLength, maxlength, email } from "vuelidate/lib/validators";

export default {
  components: {
    PictureModal,
    InfoRow,
    AlertModal
  },
  mounted() {
    this.getEmployeeTypes()
      .then(response => {
        if (response && response.status === 200) {
          response.data.forEach((employeeType) => {
            this.employeeTypes = this.employeeTypes.concat({
              value: employeeType.id,
              text: employeeType.role,
              description: employeeType.description
            });
          });
        }
      });
  },
  computed: {
    views: function() {
      let availableViews = [{
        title: "Profile",
        id: "profile"
      }];

      if (this.record && this.record.pin && this.record.id) {
        availableViews = availableViews.concat({
          title: "Reset Pin",
          id: "reset_pin"
        });
      }

      return availableViews;
    },
    firstNameValid: function() {
      return !this.$v.record.first_name.$dirty || !this.$v.record.first_name.$invalid;
    },
    lastNameValid: function() {
      return !this.$v.record.last_name.$dirty || !this.$v.record.last_name.$invalid;
    },
    emailValid: function() {
      return !this.$v.record.email.$dirty || !this.$v.record.email.$invalid;
    },
    pinValid: function() {
      return !this.$v.record.pin.$dirty || !this.$v.record.pin.$invalid;
    },
    employeeTypeIdValid: function() {
      return !this.$v.record.employee_type_id.$dirty || !this.$v.record.employee_type_id.$invalid;
    },
    firstNameHidden: function() {
      return !this.isEditing && (this.record && this.record.first_name == null);
    },
    lastNameHidden: function() {
      return !this.isEditing && (this.record && this.record.last_name == null);
    },
    emailHidden: function() {
      return !this.isEditing && (this.record && this.record.email == null);
    },
    pinHidden: function() {
      return (this.isEditing && this.isUpdating) || !this.isEditing;
    },
    roleHidden: function() {
      return !this.isEditing && (this.record && this.record.role == null);
    },
    isActiveHidden: function() {
      return !this.isEditing && (this.record && this.record.is_active == null);
    },
    config() {
      var title;
      if (!this.record || !this.record.first_name || !this.record.last_name) {
        title = "New Employee";
      } else {
        title = ((this.record.first_name) || "") + " " + (this.record.last_name || "");
      }

      return {
        title: title,
        add_button_html: "Add Employee"
      };
    },
    alertText: function() {
      return "Are you sure you want to delete " + this.record.first_name + " " + this.record.last_name;
    }
  },
  data() {
    return {
      record: {},
      employeeTypes: [],
      isEditing: false,
      isUpdating: false,

      updateFields: ["first_name", "last_name", "email", "employee_type_id", "is_active"],
      createFields: ["first_name", "last_name", "email", "employee_type_id", "is_active", "pin"],

      ignoredFields: {
        "id": true,
        "created_at": true,
        "deleted_at": true,
        "updated_at": true,
        "role": true,
        "pin": true,
        "pioneer_id": true
      }
    };
  },
  validations: {
    record: {
      first_name: {
        required,
        minLength: minLength(1)
      },
      last_name: {
        required,
        minLength: minLength(1)
      },
      email: {
        email
      },
      pin: {
        required,
        minLength: minLength(4),
        maxLength: minLength(4)
      },
      employee_type_id: {
        required
      },
      is_active: {}
    }
  },
  methods: {
    show(_event, _record) {
      if (!_record) {
        console.log("Creating new Employee...");
        this.clearForm();
        this.isEditing = true;
      } else {
        console.log("Editing Employee...");
        this.record = _record;
        this.isEditing = false;
      }
    },
    clearForm() {
      this.record = {};
    },
    edit(_record) {
      this.$refs.pictureModal.show({}, _record);
    },
    hide() {
      this.$emit("updateDone");
    },
    employeeDescriptions() {
      if (this.employeeTypes && this.record && this.record.employee_type_id) {
        return this.employeeTypes[this.record.employee_type_id - 1].description || null;
      }

      return null;
    },
    toggleEditMode() {
      if (this.isEditing === true) {
        // Validate form
        if (this.$v.$invalid) {
          this.$refs.formInvalid.open();
        }
        // Send update
        else if (this.isUpdating) {
          if (this.$v.record.$anyDirty) {
            this.updateEmployeeRecord(this.record).then(response => {
              if (response && response.status === 200) {
                this.$v.record.$reset();
                this.record = response.data.original;
                this.infoNotification(`${this.record.first_name} ${this.record.last_name} succesfully updated`)
                this.isEditing = false;
              }
            });
          } else {
            this.isEditing = false;
          }
        } else {
          this.createEmployeeRecord(this.record).then(response => {
            if (response && response.status > 199 && response.status < 300) {
              const user = response.data.original;
              this.successNotification(`${this.record.first_name} ${this.record.last_name} succesfully added`);
              this.isEditing = false;
              this.hide();
            }
          });
        }
      } else {
        if (this.record.id) {
          this.isUpdating = true;
        }
        this.isEditing = true;
      }
    },
    deleteEmployee() {
      this.$refs.confirm.open();
    },
    cancelDeleteEmployee() {
      this.$refs.confirm.close();
    },
    confirmedDeleteEmployee() {
      this.deleteEmployeeRecord(this.record).then(response => {
        if (response && response.status === 200) {
          this.hide();
          this.infoNotification(`${this.record.first_name} ${this.record.last_name} succesfully deleted`);
        }
      });
    },
    getEmployeeTypes() {
      return axios.get(`${API.BASE_URL}${API.EMPLOYEE_TYPES}`, {
        headers: {
          Authorization: `Bearer ${API.TOKEN()}`,
          Accept: "application/json"
        }
      })
        .catch(error => {
          // TODO: error message
          this.errored = true;
        });
    },
    updateEmployeeRecord(_record) {
      const id = _record.id;

      const data = {};
      this.updateFields.forEach((field) => data[field] = _record[field]);

      return axios
        .put(
          `${API.BASE_URL}${API.EMPLOYEES}/${_record.id}`, data, {
            headers: {
              Authorization: `Bearer ${API.TOKEN()}`,
              Accept: "application/json"
            }
          }
        ).catch(error => {
          this.errored = true;
          this.errorNotification(`Failed to update ${this.record.first_name} ${this.record.last_name}`)
         });
    },
    createEmployeeRecord(_record) {
      const data = {};
      this.createFields.forEach((field) => data[field] = _record[field]);

      return axios.post(
        `${API.BASE_URL}${API.EMPLOYEES}`, data, {
          headers: {
            Authorization: `Bearer ${API.TOKEN()}`,
            Accept: "application/json"
          }
        }
      ).catch(error => {
        this.errored = true;
        this.errorNotification(`Failed to create ${this.record.first_name} ${this.record.last_name}`);
      });
    },
    deleteEmployeeRecord(user) {
      const { id } = user;

      return axios
        .delete(
          `${API.BASE_URL}${API.EMPLOYEES}/${id}`, {
            headers: {
              Authorization: `Bearer ${API.TOKEN()}`,
              Accept: "application/json"
            }
          }
        ).catch(error => {
          this.errorNotification(`Failed to delete ${this.record.first_name} ${this.record.last_name}`);
        });
    }
  }
};
</script>

<style >
  .pin-reset-container {
    position: relative;
    left: 50%;
    text-align: center;
    width: 160px;
    transform: translateX(-50%);
  }

  .pin-reset-input {
    text-align: center;
  }

  .active-toggle {
    position: absolute;
    left: 0%;
    top: 3px;
    /* transform: translate(0%, -50%); */
  }

  .employee-form > .info-main-row {
    position: relative;
    padding: 2px 30px;
  }

  .modal-edit-icon {
    width: 32px;
    height: 32px;
  }

  .employee-description {
    font-size: smaller;
    font-style: italic;
  }

  .role-selector {
    width: 30%;
  }

  .employee-form {
    padding: 25px 0;
  }

  div.invalid-feedback {
    line-height: initial;
    margin: 0px;
  }

  .form-control.is-valid {
      border-color: initial;
  }
</style>
